import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const RetailerDashboard = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [business, setBusiness] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Profile update state
  const [profileData, setProfileData] = useState({
    mobile_phone: '',
    email_address: '',
    address: '',
    postcode: ''
  });

  // Discount update state
  const [discountData, setDiscountData] = useState({
    discount_type: 'flat',
    flat_discount_amount: '',
    category_discounts: {},
    discount_applicable_items: [],
    minimum_expenditure: ''
  });

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const fetchBusinessData = async () => {
    try {
      const token = localStorage.getItem('retailer_token');
      if (!token) {
        navigate('/retailer/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/retailer/business`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          localStorage.removeItem('retailer_token');
          localStorage.removeItem('retailer_user');
          navigate('/retailer/login');
          return;
        }
        throw new Error('Failed to fetch business data');
      }

      const data = await response.json();
      setBusiness(data.business);
      
      // Initialize profile data
      setProfileData({
        mobile_phone: data.business.mobile_phone || '',
        email_address: data.business.email_address || '',
        address: data.business.address || '',
        postcode: data.business.postcode || ''
      });

      // Initialize discount data
      setDiscountData({
        discount_type: data.business.discount_type || 'flat',
        flat_discount_amount: data.business.flat_discount_amount || '',
        category_discounts: data.business.category_discounts || {},
        discount_applicable_items: data.business.discount_applicable_items || [],
        minimum_expenditure: data.business.minimum_expenditure || ''
      });

      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('retailer_token');
    localStorage.removeItem('retailer_user');
    navigate('/retailer/login');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('retailer_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/retailer/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchBusinessData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDiscountUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('retailer_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/retailer/discounts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(discountData)
      });

      if (!response.ok) {
        throw new Error('Failed to update discounts');
      }

      setSuccessMessage('Discounts updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchBusinessData();
    } catch (err) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Retailer Portal</h1>
              <p className="text-sm text-gray-600">{business?.business_name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700">{successMessage}</p>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'profile', name: 'Profile', icon: '👤' },
                { id: 'discounts', name: 'Discounts', icon: '💰' },
                { id: 'subscription', name: 'Subscription', icon: '📊' },
                { id: 'jobs', name: 'Jobs & Events', icon: '📅' },
                { id: 'chatbot', name: 'Chatbot', icon: '🤖' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Business Profile</h2>
              
              {/* Company Name (Read-only) */}
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Note:</strong> Your company name cannot be edited without admin approval. 
                  Contact <a href="mailto:support@shoppersdiscountclub.directory" className="underline">support@shoppersdiscountclub.directory</a> for changes.
                </p>
              </div>

              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name (Read-only)
                  </label>
                  <input
                    type="text"
                    value={business?.business_name || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Phone
                  </label>
                  <input
                    type="tel"
                    value={profileData.mobile_phone}
                    onChange={(e) => setProfileData({...profileData, mobile_phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileData.email_address}
                    onChange={(e) => setProfileData({...profileData, email_address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={profileData.address}
                    onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postcode
                  </label>
                  <input
                    type="text"
                    value={profileData.postcode}
                    onChange={(e) => setProfileData({...profileData, postcode: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition"
                >
                  Update Profile
                </button>
              </form>
            </div>
          )}

          {activeTab === 'discounts' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Manage Discounts</h2>
              
              <form onSubmit={handleDiscountUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type
                  </label>
                  <select
                    value={discountData.discount_type}
                    onChange={(e) => setDiscountData({...discountData, discount_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="flat">Flat Monetary Discount (£)</option>
                    <option value="percentage">Category-Specific Percentage Discounts (%)</option>
                  </select>
                </div>

                {discountData.discount_type === 'flat' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flat Discount Amount (£)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={discountData.flat_discount_amount}
                      onChange={(e) => setDiscountData({...discountData, flat_discount_amount: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="10.00"
                    />
                  </div>
                )}

                {discountData.discount_type === 'percentage' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Set percentage discounts for different customer categories:</p>
                    {['students', 'pensioners', 'nhs_key_workers', 'general_public'].map((category) => (
                      <div key={category}>
                        <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                          {category.replace('_', ' ')} (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={discountData.category_discounts[category] || ''}
                          onChange={(e) => setDiscountData({
                            ...discountData,
                            category_discounts: {
                              ...discountData.category_discounts,
                              [category]: e.target.value
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="15"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Expenditure (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={discountData.minimum_expenditure}
                    onChange={(e) => setDiscountData({...discountData, minimum_expenditure: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="25.00"
                  />
                </div>

                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition"
                >
                  Update Discounts
                </button>
              </form>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Subscription Details</h2>
              
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Plan:</span>
                    <span className="text-gray-700 capitalize">{business?.selected_package || 'Standard'}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      business?.application_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {business?.application_status || 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Monthly Fee:</span>
                    <span className="text-gray-700">£{business?.monthly_fee || '15'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Chatbot URL:</span>
                    <a 
                      href={`https://${business?.generated_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline"
                    >
                      {business?.generated_url}
                    </a>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    To upgrade your plan or cancel your subscription, please contact{' '}
                    <a href="mailto:support@shoppersdiscountclub.directory" className="underline">
                      support@shoppersdiscountclub.directory
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'jobs' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Jobs & Events</h2>
              <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                <p className="text-gray-600 mb-4">Job and event posting features coming soon!</p>
                <p className="text-sm text-gray-500">
                  You'll be able to create and manage job listings and events directly from here.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'chatbot' && (
            <div>
              <h2 className="text-xl font-bold mb-4">Chatbot Content</h2>
              <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                <p className="text-gray-600 mb-4">Chatbot customization features coming soon!</p>
                <p className="text-sm text-gray-500">
                  You'll be able to update your welcome message, hours, and promotional content.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetailerDashboard;
